import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import { useTheme, alpha } from '@mui/material/styles';
import { phaseRampColor } from '../../utils/phaseColors';

/**
 * Phasenband („MilestoneTracker") — volle Breite, ruhige Ausführung.
 * Zeile: Phase + Checklisten-Chip | Segment-Pillen (Akzent-Ramp hell→dunkel) | Ziel + „Weiter →".
 * Die Checkliste der aktuellen Phase dockt als aufklappbarer Bereich direkt an
 * (Muster: Salesforce Path „Guidance for Success").
 *
 * Aktionsprinzip: nur „Weiter →" als Knopf — jede Pille ist klickbar (zurück/springen).
 * Soft-Gate: „Weiter" bei offenen Punkten öffnet zuerst die Checkliste; der zweite
 * Klick fährt trotzdem fort (kein harter Block).
 *
 * props:
 *   phases: [{ key, label, next? }]     – geordnete Phasenliste
 *   currentKey: string                  – aktueller Phasen-Key (z. B. lead.status)
 *   lost: { key, label } | null         – „verloren/abgelehnt"-Zustand (optional)
 *   onChangePhase(key)                  – Callback beim Phasenwechsel
 *   disabled: bool                      – Interaktion sperren (z. B. nach Konvertierung)
 *   successKey?: string                 – Phase, die als „gewonnen/Ziel" grün dargestellt wird
 *   sinceDate?: string(ISO)             – Zeitpunkt des letzten Phasenwechsels → „seit X Tagen"
 *   lostReason?: string                 – angezeigter Verlust-Grund (im lost-Zustand)
 *   checklistItems?: [{ label, done }]  – Checkliste der aktuellen Phase
 *   onToggleChecklistItem?(label)       – Punkt abhaken / öffnen
 */

export default function MilestoneTracker({
  phases, currentKey, lost, onChangePhase, disabled,
  successKey, sinceDate, lostReason,
  checklistItems = [], onToggleChecklistItem,
  rampFamily = 'primary', // 'support' = Blau-Ramp (Projekte & Zeit)
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false); // Soft-Gate: erster „Weiter"-Klick bei offenen Punkten

  const cEnd = theme.palette.primary.main; // Fokus-Ringe & Hervorhebung
  const cSuccess = theme.palette.success.main;
  const N = phases.length;
  const isLost = !!(lost && currentKey === lost.key);
  if (N === 0) return null; // Konfiguration (noch) leer — nichts rendern statt crashen
  let cur = phases.findIndex((p) => p.key === currentKey);
  if (cur < 0) cur = 0;
  const remaining = N - 1 - cur;
  const atSuccess = successKey && phases[cur].key === successKey;

  const total = checklistItems.length;
  const openCount = checklistItems.filter((i) => !i.done).length;
  const doneCount = total - openCount;

  if (isLost) {
    return (
      <Box sx={{ p: '14px 20px', borderRadius: '12px', bgcolor: alpha(theme.palette.error.main, 0.06) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, color: 'error.main', fontSize: '0.95rem' }}>✕ {lost.label}</Typography>
          <Button size="small" variant="outlined" disabled={disabled} onClick={() => onChangePhase(phases[0].key)}>Erneut öffnen</Button>
        </Box>
        {lostReason ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Grund: {lostReason}</Typography>
        ) : null}
      </Box>
    );
  }

  const daysInPhase = sinceDate ? Math.floor((Date.now() - new Date(sinceDate).getTime()) / 86400000) : null;
  const sinceText = daysInPhase === null ? null
    : daysInPhase <= 0 ? 'seit heute in dieser Phase'
    : daysInPhase === 1 ? 'seit 1 Tag in dieser Phase'
    : `seit ${daysInPhase} Tagen in dieser Phase`;

  const jumpTo = (key) => {
    if (disabled) return;
    setArmed(false);
    onChangePhase(key);
  };

  const goNext = () => {
    if (openCount > 0 && !armed) {
      setOpen(true);
      setArmed(true);
      return;
    }
    setArmed(false);
    setOpen(false);
    onChangePhase(phases[cur + 1].key);
  };

  // Design 3.0: Pillen in Stufen der Akzent-Ramp (hell = früh, dunkel = weit);
  // die Erfolgs-Phase bleibt semantisch grün.
  const segColor = (p, i, reached) => {
    if (!reached) return theme.palette.action.hover;
    if (successKey && p.key === successKey) return cSuccess;
    return phaseRampColor(theme, i, N, rampFamily);
  };

  const chipColor = openCount > 0 ? theme.palette.warning.main : cSuccess;

  return (
    <Box sx={{ borderRadius: '12px', bgcolor: alpha(theme.palette.primary.main, 0.045), px: 2.5, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
        {/* Meta: Phase + Checklisten-Chip + Untertitel (darf schrumpfen → Text bricht um) */}
        <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.62rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'text.secondary', fontWeight: 700 }}>
            Phase {cur + 1} / {N}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.08rem', letterSpacing: '-.01em', color: atSuccess ? 'success.main' : 'text.primary' }}>
              {phases[cur].label}
            </Typography>
            {total > 0 && (
              <Box
                role="button" tabIndex={0} aria-expanded={open}
                aria-label={`Checkliste ${doneCount} von ${total} erledigt — ${open ? 'einklappen' : 'aufklappen'}`}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
                sx={{
                  fontSize: '0.68rem', fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 1, cursor: 'pointer',
                  bgcolor: alpha(chipColor, 0.15), color: chipColor, userSelect: 'none',
                  '&:hover': { bgcolor: alpha(chipColor, 0.25) },
                  '&:focus-visible': { outline: `2px solid ${cEnd}`, outlineOffset: 1 },
                }}
              >
                Checkliste {doneCount}/{total} {open ? '▴' : '▾'}
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {[sinceText, phases[cur].next ? `Nächster Schritt: ${phases[cur].next}` : null].filter(Boolean).join(' · ')}
          </Typography>
        </Box>

        {/* Pillen */}
        <Box sx={{ display: 'flex', gap: '6px', flex: 1, minWidth: 180, alignItems: 'center' }}>
          {phases.map((p, i) => {
            const reached = i <= cur;
            const isCur = i === cur;
            return (
              <Tooltip key={p.key} title={p.label} arrow>
                <Box
                  role="button" tabIndex={disabled ? -1 : 0}
                  aria-label={`Phase ${i + 1}: ${p.label}`} aria-current={isCur ? 'step' : undefined}
                  onClick={() => jumpTo(p.key)}
                  onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); jumpTo(p.key); } }}
                  sx={{
                    flex: 1, height: 10, borderRadius: 999,
                    bgcolor: segColor(p, i, reached),
                    boxShadow: isCur ? `0 0 0 3px ${alpha(atSuccess ? cSuccess : cEnd, 0.22)}` : 'none',
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'background-color .3s, box-shadow .3s',
                    '&:hover': disabled ? {} : { filter: 'brightness(1.08)' },
                    '&:focus-visible': { outline: `2px solid ${cEnd}`, outlineOffset: 2 },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>

        {/* Ziel + Weiter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 'none' }}>
          <Typography variant="caption" sx={{ color: atSuccess ? 'success.main' : 'text.secondary', fontWeight: atSuccess ? 600 : 400 }}>
            {remaining === 0 ? `✓ ${phases[N - 1].label} erreicht` : `Ziel: ${phases[N - 1].label}`}
          </Typography>
          <Button size="small" variant="outlined" sx={{ px: 1.5, fontSize: '0.75rem', bgcolor: 'background.paper' }}
            disabled={disabled || cur >= N - 1} onClick={goNext}>
            Weiter →
          </Button>
        </Box>
      </Box>

      {/* Angedockte Checkliste */}
      {total > 0 && (
        <Collapse in={open}>
          <Box sx={{ pt: 1.25, mt: 1.25, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: '6px 22px', flexWrap: 'wrap', alignItems: 'center' }}>
            {checklistItems.map((item) => (
              <Box
                key={item.label}
                role="button" tabIndex={disabled ? -1 : 0} aria-pressed={item.done}
                onClick={() => !disabled && onToggleChecklistItem && onToggleChecklistItem(item.label)}
                onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onToggleChecklistItem && onToggleChecklistItem(item.label); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.9, py: 0.4,
                  cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
                  '&:focus-visible': { outline: `2px solid ${cEnd}`, outlineOffset: 2, borderRadius: 1 },
                }}
              >
                <Box sx={{
                  width: 18, height: 18, borderRadius: '5px', flex: 'none',
                  border: '2px solid', borderColor: item.done ? 'success.main' : 'divider',
                  bgcolor: item.done ? 'success.main' : 'transparent', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem',
                }}>
                  {item.done ? '✓' : ''}
                </Box>
                <Typography variant="body2" sx={{ color: item.done ? 'text.secondary' : 'text.primary', textDecoration: item.done ? 'line-through' : 'none' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
            {armed && openCount > 0 && (
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, ml: 'auto' }}>
                Noch {openCount} offen — „Weiter" erneut klicken, um trotzdem fortzufahren.
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
